import React from 'react';
import { Helmet } from 'react-helmet';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="space-y-6">
      <Helmet>
        <title>Politica de confidențialitate</title>
      </Helmet>

      <div className="space-y-2">
        <h1 className="text-2xl font-semibold">Politica de confidențialitate</h1>
        <p className="text-sm text-muted-foreground">Ultima actualizare: 11 februarie 2026</p>
      </div>

      <div className="rounded-lg border border-border bg-card p-4 text-sm">
        <div className="font-semibold">Operator / Furnizor</div>
        <div className="mt-2 space-y-1 text-muted-foreground">
          <div>
            <span className="text-foreground">Global Linknet SRL</span>
          </div>
          <div>Sediu: județul Argeș, Pitești, Str. Dorobanților, nr. 14, bl. 51, et. 1, ap. 8</div>
          <div>Registrul Comerțului: J03/1287/2023</div>
          <div>CUI: 48291648</div>
          <div>
            E-mail: <span className="text-foreground">office@globallinknet.ro</span>
          </div>
        </div>
      </div>

      <section className="space-y-3 text-sm">
        <h2 className="text-lg font-semibold">1. Despre această politică</h2>
        <p className="text-muted-foreground">
          Această Politică descrie modul în care sunt prelucrate datele cu caracter personal în cadrul platformei
          educaționale <span className="text-foreground">Scola</span> („Platforma”).
        </p>
        <p className="text-muted-foreground">
          Platforma poate fi utilizată în regim <span className="text-foreground">B2B</span> (de către școli/unități de
          învățământ) și în regim <span className="text-foreground">B2C</span> (de către utilizatori individuali, pe bază
          de cont/abonament, după caz).
        </p>
      </section>

      <section className="space-y-3 text-sm">
        <h2 className="text-lg font-semibold">2. Roluri GDPR: Operator vs. Împuternicit</h2>
        <div className="space-y-3 text-muted-foreground">
          <p>În funcție de implementare, rolurile pot fi diferite:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li>
              <span className="text-foreground">B2B (școală/unitate de învățământ)</span>: de regulă, unitatea de
              învățământ este <span className="text-foreground">Operator</span> pentru datele elevilor, părinților și
              personalului. Global Linknet SRL acționează ca <span className="text-foreground">Împuternicit</span> și
              prelucrează datele în numele operatorului, conform instrucțiunilor contractuale (DPA).
            </li>
            <li>
              <span className="text-foreground">B2C (utilizare individuală)</span>: Global Linknet SRL poate acționa ca{' '}
              <span className="text-foreground">Operator</span> pentru datele contului și furnizarea serviciilor.
            </li>
          </ul>
          <p>
            Dacă ai întrebări despre rolul aplicabil, ne poți contacta la{' '}
            <span className="text-foreground">office@globallinknet.ro</span>.
          </p>
        </div>
      </section>

      <section className="space-y-3 text-sm">
        <h2 className="text-lg font-semibold">3. Ce date prelucrăm</h2>
        <div className="space-y-3 text-muted-foreground">
          <p>În funcție de rol și funcționalități, Platforma poate prelucra:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li>Date de cont: nume, rol, e-mail, (după caz) username, parolă (stocată în format criptografic/hashed).</li>
            <li>Date de contact: număr de telefon (dacă este furnizat).</li>
            <li>Date profil: avatar, titulaturi (după caz).</li>
            <li>Date educaționale: clase, înscrieri, note, absențe, orar, teme/cursuri, resurse.</li>
            <li>Comunicare: mesaje, conversații, reacții, comentarii, fișiere atașate.</li>
            <li>Date tehnice: adresa IP, loguri de securitate, identificatori de sesiune, date de diagnostic.</li>
          </ul>
          <p>
            Te rugăm să nu încarci date sensibile (ex.: acte de identitate, date medicale, date bancare) în chat sau în
            resurse, decât dacă școala și cadrul legal aplicabil permit acest lucru și există un proces aprobat.
          </p>
        </div>
      </section>

      <section className="space-y-3 text-sm">
        <h2 className="text-lg font-semibold">4. Scopuri și temeiuri legale</h2>
        <div className="space-y-3 text-muted-foreground">
          <p>Prelucrăm datele pentru:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li>crearea și administrarea conturilor;</li>
            <li>furnizarea modulelor Platformei (catalog, calendar, comunicare, comunitate, bibliotecă, clasă online);</li>
            <li>securitate, prevenirea abuzurilor, audit și depanare;</li>
            <li>îndeplinirea obligațiilor legale și gestionarea solicitărilor (ex.: drepturi GDPR).</li>
          </ul>
          <p>
            Temeiul legal depinde de implementare: contract (B2C), interes legitim (securitate), obligație legală, sau (în
            B2B) instrucțiunile Operatorului (școala) și temeiurile sale legale.
          </p>
        </div>
      </section>

      <section className="space-y-3 text-sm">
        <h2 className="text-lg font-semibold">5. Unde sunt stocate datele</h2>
        <p className="text-muted-foreground">
          Datele sunt stocate pe infrastructura administrată de noi (servere proprii și/sau resurse de găzduire dedicate
          Platformei), iar accesul la date este controlat pe bază de roluri și permisiuni. Accesul tehnic al personalului
          furnizorului este limitat la situațiile necesare (mentenanță, suport, securitate) și se face cu măsuri de control
          și jurnalizare, unde este cazul.
        </p>
      </section>

      <section className="space-y-3 text-sm">
        <h2 className="text-lg font-semibold">6. Cui divulgăm datele</h2>
        <div className="space-y-3 text-muted-foreground">
          <p>Putem divulga date către:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li>
              utilizatorii autorizați din școală (profesori/admini) și, după caz, părinți/tutori – în funcție de rol și
              permisiuni;
            </li>
            <li>
              furnizori de servicii strict necesari funcționării (ex.: e-mail/SMTP), pe bază de contracte și obligații de
              confidențialitate;
            </li>
            <li>autorități publice, când legea impune.</li>
          </ul>
          <p>
            Nu vindem datele personale. Nu folosim datele elevilor pentru publicitate personalizată în Platformă.
          </p>
        </div>
      </section>

      <section className="space-y-3 text-sm">
        <h2 className="text-lg font-semibold">7. Transferuri internaționale</h2>
        <p className="text-muted-foreground">
          Nu transferăm intenționat date în afara Spațiului Economic European decât dacă este necesar și numai cu garanții
          adecvate conform GDPR (de ex.: clauze contractuale standard), acolo unde este aplicabil.
        </p>
      </section>

      <section className="space-y-3 text-sm">
        <h2 className="text-lg font-semibold">8. Perioade de stocare</h2>
        <div className="space-y-3 text-muted-foreground">
          <p>
            Păstrăm datele doar atât cât este necesar pentru scopurile descrise, conform setărilor/contractelor și cerințelor
            legale. Exemple orientative:
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li>date de cont: cât timp contul este activ și o perioadă rezonabilă pentru backup/audit;</li>
            <li>loguri de securitate: perioadă limitată, proporțională cu necesitatea de securitate;</li>
            <li>mesaje și fișiere: conform configurației Platformei (unele linkuri de descărcare pot expira automat).</li>
          </ul>
        </div>
      </section>

      <section className="space-y-3 text-sm">
        <h2 className="text-lg font-semibold">9. Securitate</h2>
        <div className="space-y-3 text-muted-foreground">
          <p>Aplicăm măsuri tehnice și organizatorice rezonabile, inclusiv:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li>control de acces pe roluri și permisiuni;</li>
            <li>parole stocate în format criptografic/hashed;</li>
            <li>limitări pentru tipuri de fișiere încărcate și mărimea acestora;</li>
            <li>jurnalizare și monitorizare (după caz) pentru prevenirea abuzurilor;</li>
            <li>copii de siguranță și proceduri de restaurare.</li>
          </ul>
          <p>
            Nicio măsură nu poate garanta securitate absolută. Dacă identificăm un incident, îl gestionăm conform obligațiilor
            legale aplicabile și procedurilor interne.
          </p>
        </div>
      </section>

      <section className="space-y-3 text-sm">
        <h2 className="text-lg font-semibold">10. Drepturile persoanelor vizate</h2>
        <div className="space-y-3 text-muted-foreground">
          <p>
            În funcție de rolul GDPR aplicabil, poți avea drepturile prevăzute de GDPR: acces, rectificare, ștergere,
            restricționare, portabilitate, opoziție și dreptul de a depune plângere.
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li>
              <span className="text-foreground">B2B</span>: solicitările privind datele educaționale sunt, de regulă,
              gestionate de Operator (școala). Te putem direcționa către școală, dacă este cazul.
            </li>
            <li>
              <span className="text-foreground">B2C</span>: ne poți contacta la{' '}
              <span className="text-foreground">office@globallinknet.ro</span>.
            </li>
          </ul>
          <p>
            Autoritatea competentă în România: <span className="text-foreground">ANSPDCP</span>.
          </p>
        </div>
      </section>

      <section className="space-y-3 text-sm">
        <h2 className="text-lg font-semibold">11. Copii și conturi de elev</h2>
        <p className="text-muted-foreground">
          Platforma poate fi utilizată de elevi minori în context educațional. Recomandăm utilizarea responsabilă și
          evitarea publicării de date personale inutile. Școlile și părinții/tutorii pot avea reguli suplimentare privind
          accesul și administrarea conturilor.
        </p>
      </section>

      <section className="space-y-3 text-sm">
        <h2 className="text-lg font-semibold">12. Cookies</h2>
        <p className="text-muted-foreground">
          Folosim cookie-uri esențiale pentru funcționarea Platformei. Pentru detalii și opțiuni, consultă Politica de
          cookies.
        </p>
      </section>

      <section className="space-y-3 text-sm">
        <h2 className="text-lg font-semibold">13. Modificări</h2>
        <p className="text-muted-foreground">
          Putem actualiza această Politică periodic. Versiunea curentă este disponibilă în Platformă.
        </p>
      </section>
    </div>
  );
};

export default PrivacyPolicy;

